import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  StarFilled,
} from "@ant-design/icons";
import { FaBed } from "react-icons/fa";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import HpmHeroGallery from "../packageProfileMockup/HpmHeroGallery";
import {
  HOTEL_PROFILE_STAGES,
  formatFx,
  formatIls,
  formatRoomDelta,
  getHotelProfileById,
} from "./mockHotelProfileData";
import "../packageProfileMockup/PackageProfileMockupPage.css";
import "./HotelProfileMockupPage.css";

function StarRating({ stars }) {
  const n = Math.max(0, Math.min(5, Number(stars) || 0));
  if (!n) return null;
  return (
    <span className="hpm-stars" aria-label={`${n} כוכבים`}>
      {Array.from({ length: n }, (_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  );
}

export default function HotelProfileMockupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hotelId = searchParams.get("hotelId");

  const hotel = useMemo(() => getHotelProfileById(hotelId), [hotelId]);
  const [roomId, setRoomId] = useState(hotel.rooms[0]?.id);
  const [termsOk, setTermsOk] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setRoomId(hotel.rooms[0]?.id);
    setTermsOk(false);
  }, [hotel.id]);

  const selectedRoom =
    hotel.rooms.find((r) => r.id === roomId) || hotel.rooms[0];

  const pricing = useMemo(() => {
    const pax = Math.max(1, Number(hotel.paxCount) || 2);
    const totalFx = Number(selectedRoom?.roomTotalFx) || Number(hotel.baseTotalUsd) || 0;
    const totalIls =
      Number(selectedRoom?.roomTotalIls) || totalFx * 3.089;
    const perFx = totalFx / pax;
    const perIls = totalIls / pax;
    return { pax, perFx, perIls, totalFx, totalIls };
  }, [hotel, selectedRoom]);

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const goBack = () => navigate("/dev/hotels-results-mockup");

  const currency = selectedRoom?.currency || hotel.currency || "USD";

  return (
    <div className="hpm-mock hpm-mock--hotel-profile" dir="rtl" lang="he">
      <header className="hpm-topbar">
        <div className="hpm-topbar__brand">
          <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="HotelsRiver" />
          <span className="hpm-pill">מוקאפ · פרופיל מלון</span>
        </div>
        <nav className="hpm-stages" aria-label="שלבי הזמנה">
          {HOTEL_PROFILE_STAGES.map((st, idx) => (
            <button
              key={st.id}
              type="button"
              className={`hpm-stage${st.active ? " is-active" : ""}${
                st.done ? " is-done" : ""
              }${st.stub ? " is-stub" : ""}`}
              disabled={st.stub}
              onClick={() => {
                if (st.id === "results") goBack();
              }}
            >
              <span className="hpm-stage__n">{idx + 1}</span>
              {st.label}
            </button>
          ))}
        </nav>
        <button type="button" className="hpm-back" onClick={goBack}>
          <LeftOutlined />
          חזרה לתוצאות
        </button>
      </header>

      <div className="hpm-layout">
        <main className="hpm-main">
          <section className="hpm-hero" id="overview">
            <div className="hpm-hero__identity">
              <p className="hpm-hero__eyebrow">מלון בלבד · בחירת חדר</p>
              <div className="hpm-hero__title-row">
                <h1>{hotel.hotelName}</h1>
                <StarRating stars={hotel.stars} />
              </div>
              <p className="hpm-hero__loc">
                <EnvironmentOutlined />
                {hotel.address || `${hotel.city}, ${hotel.country}`}
                {hotel.distanceLabel ? ` · ${hotel.distanceLabel}` : ""}
              </p>
              <div className="hpm-facts">
                <span>{hotel.dateRangeLabel}</span>
                {hotel.nights ? <span>{hotel.nights} לילות</span> : null}
                {hotel.boardLabel ? <span>{hotel.boardLabel}</span> : null}
                <span
                  className={`hpm-alloc ${
                    hotel.immediateConfirm ? "hpm-alloc--ok" : "hpm-alloc--wait"
                  }`}
                >
                  {hotel.immediateConfirm ? (
                    <>
                      <CheckCircleFilled /> אישור מיידי
                    </>
                  ) : (
                    <>
                      <ClockCircleOutlined /> נדרש אישור מלון
                    </>
                  )}
                </span>
                {hotel.refundable ? <span>ניתן לביטול</span> : null}
              </div>
              {hotel.amenities?.length ? (
                <div className="hpm-amenities">
                  {hotel.amenities.map((a) => (
                    <span key={a} className="hpm-amenity-chip">
                      {a}
                    </span>
                  ))}
                </div>
              ) : null}
              {hotel.description ? (
                <p className="hpm-hero__desc">{hotel.description}</p>
              ) : null}
            </div>
            {hotel.gallery?.length ? (
              <HpmHeroGallery images={hotel.gallery} alt={hotel.hotelName} />
            ) : null}
          </section>

          <section className="hpm-section is-emphasized" id="rooms">
            <div className="hpm-section__head">
              <div>
                <h2>בחירת חדר</h2>
                <p>
                  {hotel.rooms.length} אפשרויות · ההפרש מחושב מול חדר הבסיס
                </p>
              </div>
            </div>
            <div className="hpm-section__body">
              <div className="hpm-rooms" role="radiogroup" aria-label="בחירת חדר">
                {hotel.rooms.map((room) => {
                  const selected = roomId === room.id;
                  const delta = formatRoomDelta(room, currency);
                  return (
                    <label
                      key={room.id}
                      className={`hpm-room${selected ? " is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="hotel-mock-room"
                        checked={selected}
                        onChange={() => setRoomId(room.id)}
                      />
                      <div className="hpm-room__main">
                        <FaBed aria-hidden />
                        <div>
                          <strong>{room.name}</strong>
                          <span>
                            {room.boardLabel}
                            {room.capacityLabel ? ` · ${room.capacityLabel}` : ""}
                          </span>
                        </div>
                      </div>
                      <div className="hpm-room__price">
                        {delta ? (
                          <>
                            <span>{delta.label}</span>
                            <small>{delta.sub}</small>
                          </>
                        ) : (
                          <span className="hpm-room__included">כלול במחיר</span>
                        )}
                        <span
                          className={`hpm-alloc ${
                            room.immediateConfirm
                              ? "hpm-alloc--ok"
                              : "hpm-alloc--wait"
                          }`}
                        >
                          {room.immediateConfirm ? "אישור מיידי" : "ממתין לאישור"}
                        </span>
                        {Number.isFinite(Number(room.roomTotalFx)) ? (
                          <small className="hpm-room__total">
                            סה״כ חדר {formatFx(room.roomTotalFx, currency)}
                          </small>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </section>
        </main>

        <aside className="hpm-summary" aria-label="סיכום הזמנה">
          <div className="hpm-summary__card">
            <h2>סיכום הזמנה</h2>
            <p className="hpm-summary__hotel">{hotel.hotelName}</p>
            <ul className="hpm-summary__list">
              <li>
                <span>יעד</span>
                <strong>
                  {hotel.city}, {hotel.country}
                </strong>
              </li>
              <li>
                <span>תאריכים</span>
                <strong>{hotel.dateRangeLabel}</strong>
              </li>
              <li>
                <span>חדר</span>
                <strong>{selectedRoom?.name || "—"}</strong>
              </li>
              <li>
                <span>בסיס</span>
                <strong>{selectedRoom?.boardLabel || hotel.boardLabel}</strong>
              </li>
              <li>
                <span>הרכב</span>
                <strong>
                  {pricing.pax} מבוגרים · {hotel.roomsCount || 1} חדר
                </strong>
              </li>
            </ul>

            <div className="hpm-summary__price">
              <div className="hpm-summary__price-main">
                <span className="hpm-summary__fx">
                  {formatFx(pricing.totalFx, currency)}
                </span>
                <span className="hpm-summary__ils">
                  {formatIls(pricing.totalIls)}
                </span>
              </div>
              <div className="hpm-summary__price-meta">סה״כ לחדר</div>
              <div className="hpm-summary__total">
                לאדם{" "}
                <strong>{formatFx(pricing.perFx, currency)}</strong>
                <span>· {formatIls(pricing.perIls)}</span>
              </div>
              <p className="hpm-summary__note">
                המחיר מתעדכן לפי החדר שנבחר (כולל הפרש מול בסיס).
              </p>
            </div>

            <label className="hpm-terms">
              <input
                type="checkbox"
                checked={termsOk}
                onChange={(e) => setTermsOk(e.target.checked)}
              />
              אישור תקנון ותנאי הזמנה
            </label>

            <button
              type="button"
              className="hpm-btn-primary hpm-btn-primary--block"
              disabled={!termsOk || !selectedRoom}
              onClick={() =>
                navigate("/dev/hotels-search-mockup")
              }
            >
              אישור חדר והמשך
            </button>
            <button
              type="button"
              className="hpm-btn-ghost hpm-btn-primary--block"
              onClick={goBack}
            >
              חזרה לתוצאות
            </button>
          </div>
        </aside>
      </div>

      <div className="hpm-mobile-bar">
        <div>
          <strong>{formatFx(pricing.totalFx, currency)}</strong>
          <span>{formatIls(pricing.totalIls)}</span>
        </div>
        <button
          type="button"
          className="hpm-btn-primary"
          disabled={!termsOk || !selectedRoom}
          onClick={() => navigate("/dev/hotels-search-mockup")}
        >
          המשך
        </button>
      </div>

      {toast ? (
        <div className="hpm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
